import { useEffect, useState, type FormEvent } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type ApiError, type components } from '../../api/client'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { disablePush, enablePush, pushStatus, type PushStatus } from '../../lib/push'

// DIS-60: Settings › Notifications. Semantics mirror internal/notify:
// default level, DMs always, quiet hours in your tz, keyword triggers,
// email + digest, sounds. Changes reach other tabs via prefs.updated.

type Prefs = components['schemas']['NotificationPrefs']

export const prefsQuery = queryOptions({
  queryKey: ['prefs'],
  queryFn: async () => {
    const { data, response } = await api.GET('/me/notification-prefs')
    if (!data) throw new Error(`GET prefs failed: ${response.status}`)
    return data
  },
  staleTime: 60_000,
})

export const Route = createFileRoute('/_app/settings/notifications')({
  component: NotificationsPage,
})

function BrowserPush() {
  const [status, setStatus] = useState<PushStatus | 'loading'>('loading')
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    pushStatus()
      .then((s) => alive && setStatus(s))
      .catch(() => alive && setStatus('unsupported'))
    return () => {
      alive = false
    }
  }, [])
  const label: Record<PushStatus | 'loading', string> = {
    loading: 'Checking…',
    unsupported: 'This browser does not support push notifications.',
    disabled: 'Push is not configured on this server (VAPID keys).',
    denied: 'Notifications are blocked for this site in your browser settings.',
    off: 'Get notified in this browser when the tab is closed or hidden.',
    on: 'This browser receives push notifications.',
  }
  return (
    <section className="rounded-md border border-border p-3" data-testid="browser-push" data-status={status}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-medium">Browser notifications</p>
          <p className="text-xs text-fg-muted">{label[status]}</p>
        </div>
        {status === 'off' && (
          <Button size="sm" onClick={() => enablePush().then(setStatus).catch((e: Error) => setError(e.message))}>
            Enable
          </Button>
        )}
        {status === 'on' && (
          <Button size="sm" variant="secondary" onClick={() => disablePush().then(setStatus).catch((e: Error) => setError(e.message))}>
            Disable
          </Button>
        )}
      </div>
      {error && (
        <p role="alert" className="mt-2 text-xs text-danger">
          {error}
        </p>
      )}
    </section>
  )
}

function NotificationsPage() {
  const qc = useQueryClient()
  const prefs = useQuery(prefsQuery)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: async (body: Prefs) => {
      const { data, error: err, response } = await api.PUT('/me/notification-prefs', { body })
      if (!data) throw new Error((err as ApiError | undefined)?.fields?.prefs ?? (err as ApiError | undefined)?.message ?? `failed (${response.status})`)
      return data
    },
    onSuccess: (p) => {
      qc.setQueryData(prefsQuery.queryKey, p)
      setSaved(true)
      setError(null)
    },
    onError: (e: Error) => {
      setSaved(false)
      setError(e.message)
    },
  })
  if (!prefs.data) return <p className="p-6 text-sm text-fg-muted">{prefs.isError ? "Couldn't load preferences." : 'Loading…'}</p>
  const p = prefs.data

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const f = new FormData(e.currentTarget)
    const quiet = f.get('quiet') === 'on'
    const tz = String(f.get('tz') ?? '').trim()
    save.mutate({
      default_level: String(f.get('default_level')) as Prefs['default_level'],
      dm_always: f.get('dm_always') === 'on',
      dnd_start: quiet ? String(f.get('dnd_start')) : null,
      dnd_end: quiet ? String(f.get('dnd_end')) : null,
      tz: tz || null,
      keywords: String(f.get('keywords') ?? '')
        .split(',')
        .map((k) => k.trim())
        .filter(Boolean),
      email_enabled: f.get('email_enabled') === 'on',
      digest_hour: f.get('digest') === 'on' ? Number(f.get('digest_hour')) : null,
      sounds: f.get('sounds') === 'on',
    })
  }
  const browserTz = new Intl.DateTimeFormat().resolvedOptions().timeZone

  return (
    <div className="mx-auto max-w-xl p-6">
      <h1 className="mb-1 text-lg font-semibold">Notifications</h1>
      <p className="mb-5 text-sm text-fg-muted">What gets your attention. Muted channels still count unreads but never badge or notify.</p>
      <div className="mb-5 text-sm">
        <BrowserPush />
      </div>
      <form onSubmit={submit} className="space-y-5 text-sm" data-testid="prefs-form">
        <fieldset className="space-y-1">
          <legend className="mb-1 font-medium">Notify me about</legend>
          {(['all', 'mentions', 'none'] as const).map((lvl) => (
            <label key={lvl} className="flex items-center gap-2">
              <input type="radio" name="default_level" value={lvl} defaultChecked={p.default_level === lvl} />
              <span>{{ all: 'Every message', mentions: 'Mentions and keywords only', none: 'Nothing (badges only)' }[lvl]}</span>
            </label>
          ))}
          <p className="text-xs text-fg-muted">Channels can override this from their header menu.</p>
        </fieldset>
        <label className="flex items-center gap-2">
          <input type="checkbox" name="dm_always" defaultChecked={p.dm_always} /> Always notify for direct messages
        </label>
        <fieldset className="space-y-2">
          <label className="flex items-center gap-2">
            <input type="checkbox" name="quiet" defaultChecked={!!p.dnd_start} /> Quiet hours
          </label>
          <div className="grid grid-cols-3 gap-2 pl-6">
            <label className="text-xs text-fg-muted">
              From
              <Input name="dnd_start" type="time" defaultValue={p.dnd_start ?? '22:00'} />
            </label>
            <label className="text-xs text-fg-muted">
              Until
              <Input name="dnd_end" type="time" defaultValue={p.dnd_end ?? '08:00'} />
            </label>
            <label className="text-xs text-fg-muted">
              Time zone
              <Input name="tz" defaultValue={p.tz ?? browserTz} placeholder={browserTz} />
            </label>
          </div>
        </fieldset>
        <label className="block">
          <span className="font-medium">Keywords</span>
          <Input name="keywords" defaultValue={p.keywords.join(', ')} placeholder="deploy, oncall, my-project" className="mt-1" />
          <span className="text-xs text-fg-muted">Comma-separated; a message containing one notifies you like a mention.</span>
        </label>
        <fieldset className="space-y-2">
          <label className="flex items-center gap-2">
            <input type="checkbox" name="email_enabled" defaultChecked={p.email_enabled} /> Email me about mentions and DMs I missed while offline
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" name="digest" defaultChecked={p.digest_hour != null} /> Daily digest at
            <Input name="digest_hour" type="number" min={0} max={23} defaultValue={p.digest_hour ?? 9} className="w-20" aria-label="Digest hour" />
            <span className="text-xs text-fg-muted">:00</span>
          </label>
        </fieldset>
        <label className="flex items-center gap-2">
          <input type="checkbox" name="sounds" defaultChecked={p.sounds} /> Play a sound for new notifications
        </label>
        {error && (
          <p role="alert" className="text-danger">
            {error}
          </p>
        )}
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save preferences'}
          </Button>
          {saved && <span className="text-fg-muted">Saved.</span>}
        </div>
      </form>
    </div>
  )
}
